import React, { useState, useCallback, useRef } from 'react'
import useMpciToast from '../Common/hooks/useMpciToast'
import {
  Box, Card, CardContent,
  Grid, IconButton,
  Tooltip, Typography, makeStyles, useTheme
} from '@material-ui/core'
import moment from 'moment-timezone'
import { components, BasePage as CngBasePage, useServices } from 'cng-web-lib'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import CngSection from '../../../components/cngcomponents/CngSection'
import useBreadcrumbNameMap from 'src/useBreadcrumbNameMap'

import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import MpciTable from '../Common/components/MpciTable'
import UploadService from './UploadService'
import TruncCell from '../Common/components/TruncCell'
import ManifestIdsCell from '../Common/components/ManifestIdsCell'
import SubmitDraftsDialog from './SubmitDraftsDialog'
import EditDraftsDialog from './EditDraftsDialog'
import DeleteDraftsDialog from './DeleteDraftsDialog'


const {
  button: { CngButton }
} = components

const ACCEPTED_FORMATS = ['.xlsx']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

const useStyles = makeStyles((theme) => ({
  dropZone: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: `1.5px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': { borderColor: theme.palette.primary.main }
  },
  dropZoneActive: {
    borderColor: `${theme.palette.primary.main} !important`,
    backgroundColor: theme.palette.primary.light + '14'
  },
  dropZoneError: {
    borderColor: `${theme.palette.error.main} !important`
  },
  fileLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
    minWidth: 0
  },
  fileIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 6,
    backgroundColor: theme.palette.primary.light + '22',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main,
    fontSize: 16,
    flexShrink: 0
  },
  fileName: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  fileHint: { fontSize: 11, color: theme.palette.text.secondary },
  clearBtn: {
    color: theme.palette.text.disabled,
    padding: 4,
    '&:hover': { color: theme.palette.error.main }
  },
  chip: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
    whiteSpace: 'nowrap'
  },
    viewLog: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    color: theme.palette.primary.main,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' }
  },

}))


function FileUploadPage({ history, showNotification }) {
      const [file, setFile] = useState(null)
  const [fileError, setFileError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [downloadingUpload, setDownloadingUpload] = useState(false)
  const [downloadingUpdate, setDownloadingUpdate] = useState(false)
  const [downloadingCancel, setDownloadingCancel] = useState(false);
  const [downloadingErrorLog, setDownloadingErrorLog] = useState(null) // holds row id being downloaded

  // --- Draft action dialog state ---
  const [activeRow, setActiveRow] = useState(null)
  const [submitOpen, setSubmitOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
    const fileInputRef = useRef(null)
  const historyTableRef = useRef(null)
  const theme = useTheme()
  const classes = useStyles()
  const { securedSendRequest } = useServices()

  function openDraftDialog(row, dialog) {
    setActiveRow(row)
    if (dialog === 'submit') setSubmitOpen(true)
    else if (dialog === 'edit') setEditOpen(true)
    else if (dialog === 'delete') setDeleteOpen(true)
  }

  function closeAllDraftDialogs() {
    if (submitting) return
    setSubmitOpen(false)
    setEditOpen(false)
    setDeleteOpen(false)
    setActiveRow(null)
  }

  function refreshHistoryTable() {
    if (historyTableRef.current?.performRefresh) historyTableRef.current.performRefresh()
  }

  // --- Submit Drafts ---
  function handleSubmitDrafts() {
    if (!activeRow) return
    setSubmitting(true)
    console.log('[FileUploadPage] submitDrafts - fileUploadId:', activeRow.id)
    UploadService.submitDrafts(
      activeRow.id,
      securedSendRequest,
      () => {
        setSubmitting(false)
        setSubmitOpen(false)
        setActiveRow(null)
        if (showNotification) showNotification('success', 'Draft(s) submitted to NAIC successfully.')
        refreshHistoryTable()
      },
      (error) => {
        setSubmitting(false)
        const msg = error?.response?.data?.message || 'Error encountered submitting draft(s)'
        if (showNotification) showNotification('error', msg)
      }
    )
  }

  // --- Edit Drafts ---
  function handleEditDrafts(file) {
    if (!activeRow || !file) return
    setSubmitting(true)
    console.log('[FileUploadPage] editDrafts - fileUploadId:', activeRow.id, '| file:', file.name)
    const formData = new FormData()
    formData.append('fileUploadId', activeRow.id)
    formData.append('file', file)
    UploadService.editDrafts(
      formData,
      securedSendRequest,
      () => {
        setSubmitting(false)
        setEditOpen(false)
        setActiveRow(null)
        if (showNotification) showNotification('success', 'Draft(s) updated successfully.')
        refreshHistoryTable()
      },
      (error) => {
        setSubmitting(false)
        const msg = error?.response?.data?.message || 'Error encountered uploading/editing draft(s)'
        if (showNotification) showNotification('error', msg)
      }
    )
  }

  // --- Delete Drafts ---
  function handleDeleteDrafts() {
    if (!activeRow) return
    setSubmitting(true)
    console.log('[FileUploadPage] deleteDrafts - fileUploadId:', activeRow.id)
    UploadService.deleteDrafts(
      activeRow.id,
      securedSendRequest,
      () => {
        setSubmitting(false)
        setDeleteOpen(false)
        setActiveRow(null)
        if (showNotification) showNotification('success', 'Draft(s) deleted successfully.')
        refreshHistoryTable()
      },
      (error) => {
        setSubmitting(false)
        const msg = error?.response?.data?.message || 'Error encountered deleting draft(s)'
        if (showNotification) showNotification('error', msg)
      }
    )
  }

  const isCompStatus = (row) => ['COMP', 'COMPLETED', 'BILLS_CREATED'].includes(row.status)

  const hasManifestIds = (row) => Array.isArray(row.manifestHeaderIds) && row.manifestHeaderIds.length > 0

  const isActionEnabled = (row) => hasManifestIds(row) && (isCompStatus(row) || row.status === 'FAIL')

    const validateFile = (f) => {
    if (!f) return 'Please select a file.'
    const ext = '.' + f.name.split('.').pop().toLowerCase()
    if (!ACCEPTED_FORMATS.includes(ext)) return 'Invalid format. Only .xlsx files are accepted'
    if (f.size > MAX_FILE_SIZE) return 'File exceeds 10 MB limit.'
    return ''
  }

    const handleFileSelect = useCallback((e) => {
    const f = e.target.files[0]
    if (!f) return
    const err = validateFile(f)
    setFileError(err)
    setFile(err ? null : f)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (!f) return
    const err = validateFile(f)
    setFileError(err)
    setFile(err ? null : f)
  }, [])

    const handleUpload = () => {
    const err = validateFile(file)
    if (err) { setFileError(err); return }

    const formData = new FormData()
    formData.append('file', file)

    setUploading(true)
    UploadService.upload(
      formData,
      securedSendRequest,
      () => {
        setUploading(false)
        setFile(null)
        if (showNotification) showNotification('success', 'File uploaded successfully.')
        if (historyTableRef.current?.performRefresh) historyTableRef.current.performRefresh()
      },
      (error) => {
        setUploading(false)
        const msg = error?.response?.data?.message || 'Upload failed. Please try again.'
        if (showNotification) showNotification('error', msg)
      }
    )
  }

  const handleDownloadTemplate = useCallback((templateKey) => {
    const loadingMap = {
      UPLOAD: setDownloadingUpload,
      UPDATE: setDownloadingUpdate,
      CANCEL: setDownloadingCancel
    }
    const setLoading = loadingMap[templateKey]
  
    setLoading(true)
  
    UploadService.downloadTemplateByKey(
      templateKey,
      securedSendRequest,
      (response) => {
        setLoading(false)
  
        try {
          const blob = response.data
          let fileName = 'download.xlsx'
  
          const headers = response.headers || {}
          const contentDisposition =
            headers['content-disposition'] ||
            headers['Content-Disposition']
  
          if (contentDisposition) {
            const match = contentDisposition.match(
              /filename\*?=(?:UTF-8'')?"?([^"]+)"?/i
            )
            if (match && match[1]) {
              fileName = decodeURIComponent(match[1])
            }
          }
  
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = fileName
  
          document.body.appendChild(a)
          a.click()
          a.remove()
  
          setTimeout(() => {
            window.URL.revokeObjectURL(url)
          }, 100)
        } catch (e) {
          if (showNotification) {
            showNotification('error', 'Failed to download file.')
          }
        }
      },
      (error) => {
        setLoading(false)
  
        const msg =
          error?.response?.data?.message ||
          'Template download failed.'
  
        if (showNotification) {
          showNotification('error', msg)
        }
      }
    )
  }, [securedSendRequest, showNotification])

          const handleDownloadErrorLog = useCallback((row) => {
    setDownloadingErrorLog(row.id)
    UploadService.downloadErrorFile(
      row.id,
      securedSendRequest,
      (response) => {
        setDownloadingErrorLog(null)
        try {
          const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'text/plain' })
          const disposition = response.headers?.['content-disposition'] || ''
          const match = disposition.match(/filename="?([^"]+)"?/)
                      const fileName = match ? match[1] : `error_log_${row.id}.txt`
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = fileName
          document.body.appendChild(a)
          a.click()
          a.remove()
          window.URL.revokeObjectURL(url)
        } catch {
          if (showNotification) showNotification('error', 'Failed to process error log file.')
        }
      },
      (error) => {
        setDownloadingErrorLog(null)
        const msg = error?.response?.data?.message || 'Failed to download error log.'
        if (showNotification) showNotification('error', msg)
      }
    )
  }, [securedSendRequest, showNotification])

          // Covers both full names and API abbreviations
  const statusColorMap = {
    // Γ£ö Success family — COMP / COMPLETED = Bill(s) Created
    COMP:              { bg: theme.palette.success.light, text: theme.palette.success.dark,  label: "Bill(s) Created" },
    COMPLETED:         { bg: theme.palette.success.light, text: theme.palette.success.dark,  label: "Bill(s) Created" },
    BILLS_CREATED:     { bg: theme.palette.success.light, text: theme.palette.success.dark,  label: "Bill(s) Created" },
    // Γä╣ Info / in-progress family — PROC / PROCESSING = Processing
    PROC:              { bg: theme.palette.info.light,    text: theme.palette.info.dark,     label: 'Processing' },
    PROCESSING:        { bg: theme.palette.info.light,    text: theme.palette.info.dark,     label: 'Processing' },
    IN_PROGRESS:       { bg: theme.palette.info.light,    text: theme.palette.info.dark,     label: 'Processing' },
    // ΓÜá Warning / received family — RECV / RECEIVED = Received
    RECV:              { bg: theme.palette.warning.light, text: theme.palette.warning.dark,  label: 'Received' },
    RECEIVED:          { bg: theme.palette.warning.light, text: theme.palette.warning.dark,  label: 'Received' },
    PENDING:           { bg: theme.palette.warning.light, text: theme.palette.warning.dark,  label: 'Pending' },
    PEND:              { bg: theme.palette.warning.light, text: theme.palette.warning.dark,  label: 'Pending' },
    // Γ¥î Error family — FAIL / FAILED = Processing Error
    FAIL:              { bg: theme.palette.error.light,   text: theme.palette.error.dark,    label: 'Processing Error' },
    FAILED:            { bg: theme.palette.error.light,   text: theme.palette.error.dark,    label: 'Processing Error' },
    PROCESSING_ERROR:  { bg: theme.palette.error.light,   text: theme.palette.error.dark,    label: 'Processing Error' },
    ERR:               { bg: theme.palette.error.light,   text: theme.palette.error.dark,    label: 'Processing Error' }
  }

    const columns = [
    { field: 'id', sortKey: 'id', title: 'File Upload System ID', render: (row) => <TruncCell value={row.id} /> },
    { field: 'fileName', sortKey: 'fileName', title: 'File Name' },
    {
      field: 'manifestHeaderId',
      sortKey: 'manifestHeaderId',
      title: 'Master/House Bill System ID(s)',
      render: (row) => <ManifestIdsCell ids={row.manifestHeaderIds} />
    },
    {
            field: 'status', sortKey: 'status', title: 'Status',
      render: (row) => {
        const c = statusColorMap[row.status] || { bg: theme.palette.background.sectionOddBg, text: theme.palette.text.secondary, label: row.status }
        return (
                    <span className={classes.chip} style={{ backgroundColor: c.bg, color: c.text }}>
            {c.label}
          </span>
        )
      }
    },
        {
            field: 'errorFilePath', sortKey: 'errorFilePath', title: 'Error Log',
        render: (row) => {
          if (!row.errorFilePath) return <span style={{ color: theme.palette.text.disabled }}>—</span>
        const isLoading = downloadingErrorLog === row.id
        return (
          <span
            className={classes.viewLog}
            style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.6 : 1 }}
            onClick={() => !isLoading && handleDownloadErrorLog(row)}
          >
            <FontAwesomeIcon
              icon={['fal', isLoading ? 'spinner' : 'file-lines']}
              spin={isLoading}
            />
            {isLoading ? 'Downloading...' : 'Download Log'}
          </span>
        )
      }
    },
    {
      field: 'createdDate', sortKey: 'createdDate', title: 'Created Date',
      render: (row) => row.createdDate ? moment(row.createdDate).format('D-MMM-YYYY HH:mm') : '—'
    }
  ]

    const filters = [
    { name: 'id',                label: 'File Upload System ID',       type: 'text' },
    { name: 'fileName',          label: 'File Name',                   type: 'text' },
    { name: 'manifestHeaderId',  label: 'Master/House Bill System ID', type: 'text' },
        { name: 'status',           label: 'Status',             type: 'select', options: [
          { value: 'RECV',             label: 'Received' },
          { value: 'PROC',             label: 'Processing' },
          { value: 'COMP',             label: "Bill(s) Created" },
          { value: 'FAIL',             label: 'Processing Error' }
        ]},
    { name: 'createdDate', label: 'Created Date', type: 'daterange' }
  ]

                      return (
    <>
    <Card>
      <Box padding={2}>
        {/* Download Templates */}
		<CngSection title='Download Templates'>
		  <CardContent>
		    <Grid container spacing={2}>
		      <Grid item>
		        <CngButton
		          color='primary'
		          size='medium'
		          disabled={downloadingUpload}
		          onClick={() => handleDownloadTemplate('UPLOAD')}
		          startIcon={
		            <FontAwesomeIcon
		              icon={['fal', downloadingUpload ? 'spinner' : 'file-arrow-down']}
		              spin={downloadingUpload}
		            />
		          }
		        >
		          {downloadingUpload
		            ? 'Downloading...'
		            : 'Download Template for New Bills'}
		        </CngButton>
		      </Grid>
		
		      <Grid item>
		        <CngButton
		          color='primary'
		          size='medium'
		          disabled={downloadingUpdate}
		          onClick={() => handleDownloadTemplate('UPDATE')}
		          startIcon={
		            <FontAwesomeIcon
		              icon={['fal', downloadingUpdate ? 'spinner' : 'file-arrow-down']}
		              spin={downloadingUpdate}
		            />
		          }
		        >
		          {downloadingUpdate
		            ? 'Downloading...'
		            : 'Download Template for Updating Bills'}
		        </CngButton>
		      </Grid>
		
		      <Grid item>
		        <CngButton
		          color='primary'
		          size='medium'
		          disabled={downloadingCancel}
		          onClick={() => handleDownloadTemplate('CANCEL')}
		          startIcon={
		            <FontAwesomeIcon
		              icon={['fal', downloadingCancel ? 'spinner' : 'file-arrow-down']}
		              spin={downloadingCancel}
		            />
		          }
		        >
		          {downloadingCancel
		            ? 'Downloading...'
		            : 'Download Template for Cancellation and RFI - Information cannot be provided'}
		        </CngButton>
		      </Grid>
		    </Grid>
		  </CardContent>
		</CngSection>

        {/* Upload Excel File */}
        <Box mt={2}>
          <CngSection title='Upload Excel File'>
            <CardContent>
              <input ref={fileInputRef} type='file' accept='.xlsx'
                onChange={handleFileSelect} style={{ display: 'none' }} />

              <Box
                className={[
                  classes.dropZone,
                  dragOver ? classes.dropZoneActive : '',
                  fileError ? classes.dropZoneError : ''
                ].filter(Boolean).join(' ')}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <Box className={classes.fileLabel}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  <Box className={classes.fileIconWrap}>
                    <FontAwesomeIcon icon={['fal', file ? 'file-excel' : 'cloud-arrow-up']} />
                  </Box>
                  <Box style={{ minWidth: 0 }}>
                    <Typography className={classes.fileName}>
                      {file ? file.name : 'Choose a file or drag & drop here'}
                    </Typography>
                    <Typography className={classes.fileHint}>
                      {file ? `${(file.size / 1024).toFixed(1)} KB \u2022 Click to change` : '.xlsx \u2022 Max 10 MB'}
                    </Typography>
                  </Box>
                </Box>
                <Box display='flex' alignItems='center' style={{ gap: 8, flexShrink: 0 }}>
                  {file && (
                    <IconButton size='small' className={classes.clearBtn}
                      onClick={() => { setFile(null); setFileError('') }}
                    >
                      <FontAwesomeIcon icon={['fal', 'xmark']} />
                    </IconButton>
                  )}
                  <CngButton color='primary' size='medium'
                    disabled={uploading || !file} onClick={handleUpload}
                    startIcon={<FontAwesomeIcon icon={['fal', uploading ? 'spinner' : 'upload']} spin={uploading} />}
                  >
                    {uploading ? 'Uploading...' : 'Upload File'}
                  </CngButton>
                </Box>
              </Box>

              {fileError && (
                <Typography variant='caption' color='error' style={{ marginTop: 6, display: 'block' }}>
                  <FontAwesomeIcon icon={['fal', 'circle-exclamation']} style={{ marginRight: 4 }} />
                  {fileError}
                </Typography>
              )}
            </CardContent>
          </CngSection>
        </Box>

        {/* MPCI Bill Upload Records */}
        <Box mt={2}>
          <CngSection title='Excel Upload Records'>
            <MpciTable
              ref={historyTableRef}
              flat
              url={UaeMpciApiUrls.EXCEL_UPLOAD_LIST}
              columns={columns}
              filters={filters}
              rowActions={[
                {
                  icon: <FontAwesomeIcon icon={['fal', 'paper-plane']} />,
                  label: 'Submit to NAIC',
                  onClick: (row) => openDraftDialog(row, 'submit'),
                  hidden: (row) => !isActionEnabled(row)
                },
                {
                  icon: <FontAwesomeIcon icon={['fal', 'pen-to-square']} />,
                  label: 'Edit Draft',
                  onClick: (row) => openDraftDialog(row, 'edit'),
                  hidden: (row) => !isActionEnabled(row)
                },
                {
                  icon: <FontAwesomeIcon icon={['fal', 'trash-can']} />,
                  label: 'Delete Draft',
                  onClick: (row) => openDraftDialog(row, 'delete'),
                  hidden: (row) => !isActionEnabled(row)
                }
              ]}
              defaultSort={{ field: 'createdDate', direction: 'DESC' }}
            />
          </CngSection>
        </Box>
      </Box>
    </Card>

      {/* Draft Action Dialogs */}
      <SubmitDraftsDialog
        open={submitOpen}
        row={activeRow}
        onClose={closeAllDraftDialogs}
        onConfirm={handleSubmitDrafts}
        submitting={submitting}
      />

      <EditDraftsDialog
        open={editOpen}
        row={activeRow}
        onClose={closeAllDraftDialogs}
        onConfirm={handleEditDrafts}
        submitting={submitting}
      />

      <DeleteDraftsDialog
        open={deleteOpen}
        row={activeRow}
        onClose={closeAllDraftDialogs}
        onConfirm={handleDeleteDrafts}
        submitting={submitting}
      />
    </>
  )
}

function WrappedFileUploadPage(props) {
  const breadcrumbNameMap = useBreadcrumbNameMap()
  const { showNotification, toastNode } = useMpciToast()
  return (
    <>
      <CngBasePage
        renderPage={() => <FileUploadPage showNotification={showNotification} {...props} />}
        extraCngBreadcrumbNameMap={breadcrumbNameMap}
      />
      {toastNode}
    </>
  )
}

export default WrappedFileUploadPage